/**
 * Compact appointment-dialog UI.
 * Minimal chrome: no header, no icons, no visible labels where the placeholder
 * or input type makes the purpose obvious. Wider canvas, larger text,
 * two-column layout, aiming to eliminate vertical scrolling.
 */

import { useEffect, useState } from 'react'
import { Trash2, CheckCheck, Check, Search, X, ExternalLink, ChevronUp, ChevronDown, Eye, EyeOff } from 'lucide-react'
import { format, parse, parseISO, isValid, addDays } from 'date-fns'
import {
	Dialog,
	DialogContent,
	DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { ColorPicker } from '@/components/shared/ColorPicker'
import { DEFAULT_EVENT_COLOR } from '@/lib/constants'
import { MAX_PP_PER_DAY } from '@/config'
import type { AppointmentDialogState } from '../useAppointmentDialog'
import { GhostDayColumn } from '../GhostDayColumn'
import type { AppointmentType } from '@/types/calendar'

const GHOST_CALENDAR_TYPE: Partial<Record<AppointmentType, AppointmentType>> = {
	ZUB: 'DH',
	DH: 'ZUB',
}

const HOURS_IN_DAY = 24
const MINUTES_STEP = 15

function parseTime(value: string | undefined): { hours: string; minutes: string } {
	const [h = '', m = ''] = (value ?? '').split(':')
	return { hours: h, minutes: m }
}

function pad(n: number): string {
	return String(n).padStart(2, '0')
}

interface TimeSpinnerProps {
	value: string | undefined
	onChange: (next: string) => void
	hasError?: boolean
	ariaLabel?: string
}

function TimeSpinner({ value, onChange, hasError, ariaLabel }: TimeSpinnerProps) {
	const { hours, minutes } = parseTime(value)

	const stepHours = (delta: number) => {
		const current = parseInt(hours, 10) || 0
		const next = ((current + delta) % HOURS_IN_DAY + HOURS_IN_DAY) % HOURS_IN_DAY
		onChange(`${pad(next)}:${minutes || '00'}`)
	}

	const stepMinutes = (delta: number) => {
		const current = parseInt(minutes, 10) || 0
		const rounded = Math.round(current / MINUTES_STEP) * MINUTES_STEP
		const next = ((rounded + delta * MINUTES_STEP) % 60 + 60) % 60
		onChange(`${hours || '00'}:${pad(next)}`)
	}

	const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const digits = e.target.value.replace(/\D/g, '').slice(0, 2)
		onChange(`${digits}:${minutes || '00'}`)
	}

	const handleHoursBlur = () => {
		const num = Math.max(0, Math.min(23, parseInt(hours, 10) || 0))
		onChange(`${pad(num)}:${minutes || '00'}`)
	}

	const handleMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const digits = e.target.value.replace(/\D/g, '').slice(0, 2)
		onChange(`${hours || '00'}:${digits}`)
	}

	const handleMinutesBlur = () => {
		const num = Math.max(0, Math.min(59, parseInt(minutes, 10) || 0))
		onChange(`${hours || '00'}:${pad(num)}`)
	}

	const handleHoursWheel = (e: React.WheelEvent<HTMLInputElement>) => {
		if (document.activeElement !== e.currentTarget) return
		e.preventDefault()
		stepHours(e.deltaY < 0 ? 1 : -1)
	}

	const handleMinutesWheel = (e: React.WheelEvent<HTMLInputElement>) => {
		if (document.activeElement !== e.currentTarget) return
		e.preventDefault()
		stepMinutes(e.deltaY < 0 ? 1 : -1)
	}

	const handleHoursKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'ArrowUp') {
			e.preventDefault()
			stepHours(1)
		} else if (e.key === 'ArrowDown') {
			e.preventDefault()
			stepHours(-1)
		}
	}

	const handleMinutesKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'ArrowUp') {
			e.preventDefault()
			stepMinutes(1)
		} else if (e.key === 'ArrowDown') {
			e.preventDefault()
			stepMinutes(-1)
		}
	}

	const arrowBtn =
		'flex items-center justify-center w-9 h-6 text-[#8c8275] hover:text-[#d7ad6c] hover:bg-[#f5ebe0] rounded-md transition-colors cursor-pointer active:bg-[#ecdcc1]'
	const segment = 'flex flex-col items-center select-none'
	const numInput =
		'w-8 h-6 text-center text-sm font-semibold text-[#3d3428] bg-transparent border-none outline-none focus:outline-none tabular-nums caret-[#d7ad6c] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'

	return (
		<div
			role="group"
			aria-label={ariaLabel}
			className={`inline-flex items-center gap-0.5 px-1.5 py-1 rounded-xl border bg-[#faf8f5] transition-colors focus-within:border-[#d7ad6c] ${
				hasError ? 'border-red-400' : 'border-[#e8e4df]'
			}`}
		>
			<div className={segment}>
				<button
					type="button"
					tabIndex={-1}
					onClick={() => stepHours(1)}
					className={arrowBtn}
					aria-label="Zvýšit hodiny"
				>
					<ChevronUp className="h-3.5 w-3.5" strokeWidth={2.5} />
				</button>
				<input
					type="text"
					inputMode="numeric"
					value={hours}
					onChange={handleHoursChange}
					onBlur={handleHoursBlur}
					onFocus={(e) => e.currentTarget.select()}
					onWheel={handleHoursWheel}
					onKeyDown={handleHoursKeyDown}
					className={numInput}
					aria-label="Hodiny"
					maxLength={2}
				/>
				<button
					type="button"
					tabIndex={-1}
					onClick={() => stepHours(-1)}
					className={arrowBtn}
					aria-label="Snížit hodiny"
				>
					<ChevronDown className="h-3.5 w-3.5" strokeWidth={2.5} />
				</button>
			</div>
			<span className="text-sm font-semibold text-[#5c5142] -mx-0.5">:</span>
			<div className={segment}>
				<button
					type="button"
					tabIndex={-1}
					onClick={() => stepMinutes(1)}
					className={arrowBtn}
					aria-label="Zvýšit minuty"
				>
					<ChevronUp className="h-3.5 w-3.5" strokeWidth={2.5} />
				</button>
				<input
					type="text"
					inputMode="numeric"
					value={minutes}
					onChange={handleMinutesChange}
					onBlur={handleMinutesBlur}
					onFocus={(e) => e.currentTarget.select()}
					onWheel={handleMinutesWheel}
					onKeyDown={handleMinutesKeyDown}
					className={numInput}
					aria-label="Minuty"
					maxLength={2}
				/>
				<button
					type="button"
					tabIndex={-1}
					onClick={() => stepMinutes(-1)}
					className={arrowBtn}
					aria-label="Snížit minuty"
				>
					<ChevronDown className="h-3.5 w-3.5" strokeWidth={2.5} />
				</button>
			</div>
		</div>
	)
}

interface DateSpinnerProps {
	value: string | undefined
	onChange: (next: string) => void
	hasError?: boolean
	ariaLabel?: string
}

const DATE_DISPLAY_FORMAT = 'dd.MM.yyyy'
const DATE_API_FORMAT = 'yyyy-MM-dd'

function DateSpinner({ value, onChange, hasError, ariaLabel }: DateSpinnerProps) {
	const parsed = value ? parseISO(value) : null
	const displayValue = parsed && isValid(parsed) ? format(parsed, DATE_DISPLAY_FORMAT) : value ?? ''

	const [draft, setDraft] = useState(displayValue)

	useEffect(() => {
		setDraft(displayValue)
	}, [displayValue])

	const stepDay = (delta: number) => {
		const base = value ? parseISO(value) : null
		if (!base || !isValid(base)) return
		const next = addDays(base, delta)
		onChange(format(next, DATE_API_FORMAT))
	}

	const commit = () => {
		const candidate = parse(draft, DATE_DISPLAY_FORMAT, new Date())
		if (isValid(candidate)) {
			onChange(format(candidate, DATE_API_FORMAT))
		} else {
			setDraft(displayValue)
		}
	}

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'ArrowUp') {
			e.preventDefault()
			stepDay(1)
		} else if (e.key === 'ArrowDown') {
			e.preventDefault()
			stepDay(-1)
		} else if (e.key === 'Enter') {
			e.preventDefault()
			e.currentTarget.blur()
		}
	}

	const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
		if (document.activeElement !== e.currentTarget) return
		e.preventDefault()
		stepDay(e.deltaY < 0 ? 1 : -1)
	}

	const arrowBtn =
		'flex items-center justify-center w-full h-6 text-[#8c8275] hover:text-[#d7ad6c] hover:bg-[#f5ebe0] rounded-md transition-colors cursor-pointer active:bg-[#ecdcc1]'
	const dateInput =
		'w-[136px] h-6 text-center text-sm font-semibold text-[#3d3428] bg-transparent border-none outline-none focus:outline-none tabular-nums caret-[#d7ad6c]'

	return (
		<div
			role="group"
			aria-label={ariaLabel}
			className={`inline-flex flex-col items-stretch gap-0 px-1.5 py-1 rounded-xl border bg-[#faf8f5] transition-colors focus-within:border-[#d7ad6c] ${
				hasError ? 'border-red-400' : 'border-[#e8e4df]'
			}`}
		>
			<button
				type="button"
				tabIndex={-1}
				onClick={() => stepDay(1)}
				className={arrowBtn}
				aria-label="Další den"
			>
				<ChevronUp className="h-3.5 w-3.5" strokeWidth={2.5} />
			</button>
			<input
				type="text"
				inputMode="numeric"
				value={draft}
				onChange={(e) => setDraft(e.target.value)}
				onBlur={commit}
				onFocus={(e) => e.currentTarget.select()}
				onKeyDown={handleKeyDown}
				onWheel={handleWheel}
				className={dateInput}
				aria-label="Datum"
				placeholder="DD.MM.RRRR"
				maxLength={10}
			/>
			<button
				type="button"
				tabIndex={-1}
				onClick={() => stepDay(-1)}
				className={arrowBtn}
				aria-label="Předchozí den"
			>
				<ChevronDown className="h-3.5 w-3.5" strokeWidth={2.5} />
			</button>
		</div>
	)
}

export function AppointmentDialogCompact(state: AppointmentDialogState) {
	const {
		open,
		onClose,
		mode,
		viewMode,
		register,
		errors,
		setValue,
		submitForm,
		background,
		selectedDate,
		startTime,
		endTime,
		handleStartTimeChange,
		handleEndTimeChange,
		isLoading,
		isLoadingDetails,
		isDeleting,
		isSearching,
		isEventToday,
		isNoShowBackground,
		isCancelledBg,
		isPPDisabled,
		actionTypes,
		selectedPatients,
		patientData,
		filteredPatients,
		patientsListRef,
		showPatientsFade,
		checkPatientsFade,
		handleAddPatient,
		handleRemovePatient,
		handlePatientDataChange,
		patientQuery,
		setPatientQuery,
		patientDropdownOpen,
		setPatientDropdownOpen,
		patientSelectedIndex,
		setPatientSelectedIndex,
		handlePatientKeyDown,
		patientsToDailyNotes,
		dailyNotesStatus,
		handleToggleDailyNotes,
		noShowUpPaid,
		setNoShowUpPaid,
		sendCancelSms,
		setSendCancelSms,
		cancelType,
		setCancelType,
		cancelSmsData,
		handleDelete,
		calendarType,
		showGhostColumn,
		toggleGhostColumn,
	} = state

	const ghostCalendarType = GHOST_CALENDAR_TYPE[calendarType]

	const inputBase =
		'text-[#3d3428] bg-[#faf8f5] border border-[#e8e4df] rounded-xl focus:border-[#d7ad6c] focus:outline-none transition-colors'

	return (
		<Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
			<DialogContent className={`${showGhostColumn && ghostCalendarType ? 'max-w-7xl' : 'max-w-6xl'} flex flex-col !bg-[#f0ece8] p-3 gap-0`}>
				<DialogTitle className="sr-only">
					{mode === 'create' ? 'Nový termín' : 'Upravit termín'}
				</DialogTitle>

				<div className="flex gap-3">
					<div className="flex-1 min-w-0 bg-white rounded-2xl px-5 py-4 shadow-sm">
				{mode === 'edit' && isLoadingDetails && (
					<div className="flex items-center justify-center py-12">
						<div className="flex items-center gap-3 text-[#8c8275]">
							<div className="w-5 h-5 border-2 border-[#d7ad6c] border-t-transparent rounded-full animate-spin" />
							<span className="text-base">Načítání termínu...</span>
						</div>
					</div>
				)}

				<form
					onSubmit={submitForm}
					className={`space-y-3 ${mode === 'edit' && isLoadingDetails ? 'hidden' : ''}`}
				>
					{/* Title + optional no-show chip */}
					<div className="flex items-start gap-2 pr-10">
						<Textarea
							id="title"
							placeholder="Název termínu..."
							{...register('title')}
							rows={2}
							className={`${inputBase} flex-1 text-xl font-semibold leading-tight resize-none px-4 py-3 ${errors.title ? 'border-red-400' : ''}`}
							style={{ minHeight: '64px', maxHeight: '64px' }}
						/>
						{ghostCalendarType && (
							<button
								type="button"
								onClick={toggleGhostColumn}
								title={showGhostColumn ? `Skrýt termíny ${ghostCalendarType}` : `Zobrazit termíny ${ghostCalendarType}`}
								className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 rounded-xl border text-xs font-medium transition-colors self-stretch ${
									showGhostColumn
										? 'bg-[#f5ebe0] border-[#d7ad6c] text-[#d7ad6c]'
										: 'bg-white border-[#e8e4df] text-[#8c8275] hover:bg-[#faf8f5] hover:border-[#d7ad6c] hover:text-[#d7ad6c]'
								}`}
							>
								{showGhostColumn ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
								{ghostCalendarType}
							</button>
						)}
					</div>
						{errors.title && (
							<p className="text-sm text-red-500 mt-1">{errors.title.message}</p>
						)}
						{isNoShowBackground && (
							<button
								type="button"
								onClick={() => setNoShowUpPaid(!noShowUpPaid)}
								className="flex items-center gap-3 mt-2 px-3 py-2 rounded-xl bg-red-50 border border-red-200 text-left hover:bg-red-100 transition-colors cursor-pointer w-fit"
							>
								<div
									className={`w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center ${
										noShowUpPaid ? 'bg-red-500 border-red-500' : 'bg-white border-red-400'
									}`}
								>
									{noShowUpPaid && <span className="text-white text-sm font-bold">✓</span>}
								</div>
								<span className="text-base text-red-700">Sankce uhrazena</span>
							</button>
						)}

					{/* Row: date / time */}
					<div className="flex items-center gap-3">
						<DateSpinner
							value={selectedDate}
							onChange={(v) => setValue('date', v, { shouldValidate: true, shouldDirty: true })}
							hasError={!!errors.date}
							ariaLabel="Datum termínu"
						/>
						<TimeSpinner
							value={startTime}
							onChange={handleStartTimeChange}
							hasError={!!errors.startTime}
							ariaLabel="Začátek termínu"
						/>
						<span className="text-[#8c8275] text-base">–</span>
						<TimeSpinner
							value={endTime}
							onChange={handleEndTimeChange}
							hasError={!!errors.endTime}
							ariaLabel="Konec termínu"
						/>
						{errors.endTime?.message && (
							<span className="text-sm text-red-500">{errors.endTime.message}</span>
						)}
					</div>

					{/* Patient section: search + selected list, grouped as one card */}
					<div className="rounded-xl border border-[#e8e4df] bg-[#faf8f5]/40 p-3 space-y-2">
						<div className="relative">
							<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8c8275]" />
							<Input
								type="text"
								placeholder="Vyhledat pacienta..."
								value={patientQuery}
								onChange={(e) => {
									setPatientQuery(e.target.value)
									setPatientDropdownOpen(e.target.value.length >= 2)
									setPatientSelectedIndex(0)
								}}
								onFocus={() => patientQuery.length >= 2 && setPatientDropdownOpen(true)}
								onKeyDown={handlePatientKeyDown}
								className={`h-12 pl-10 pr-10 text-base text-[#3d3428] bg-white border border-[#e8e4df] rounded-xl focus:border-[#d7ad6c] focus:outline-none transition-colors`}
							/>
							{patientQuery && (
								<button
									type="button"
									onClick={() => {
										setPatientQuery('')
										setPatientDropdownOpen(false)
									}}
									className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-[#8c8275] hover:text-[#5c5142] hover:bg-[#f5ebe0] transition-colors"
								>
									<X className="h-4 w-4" />
								</button>
							)}

							{patientDropdownOpen && patientQuery.length >= 2 && (
								<div className="absolute z-50 mt-1 w-full rounded-xl border border-[#e8e4df] bg-white shadow-lg overflow-hidden">
									{isSearching ? (
										<div className="flex items-center justify-center p-3">
											<div className="h-4 w-4 animate-spin rounded-full border-2 border-[#d7ad6c] border-t-transparent" />
											<span className="ml-2 text-base text-[#8c8275]">Vyhledávání...</span>
										</div>
									) : filteredPatients.length === 0 ? (
										<div className="p-3 text-center text-base text-[#8c8275]">
											Žádný pacient nenalezen
										</div>
									) : (
										<ul
											className="max-h-48 overflow-y-auto py-1"
											style={{ scrollbarWidth: 'thin', scrollbarColor: '#d7ad6c #f5ebe0' }}
										>
											{filteredPatients.map((patient, index) => (
												<li key={patient.id}>
													<button
														type="button"
														onClick={() => handleAddPatient(patient)}
														className={`w-full px-4 py-2.5 text-left text-base transition-colors ${
															index === patientSelectedIndex
																? 'bg-[#f5ebe0] text-[#3d3428]'
																: 'hover:bg-[#faf8f5] text-[#3d3428]'
														}`}
													>
														<span className="font-medium">
															{patient.lastName} {patient.firstName}
														</span>
														<span className="ml-2 text-[#8c8275]">- {patient.personalId}</span>
													</button>
												</li>
											))}
										</ul>
									)}
								</div>
							)}
						</div>

						{selectedPatients.length > 0 && (
							<div className="relative pt-2 border-t border-[#e8e4df]">
							<div
								ref={patientsListRef}
								onScroll={checkPatientsFade}
								className="space-y-2 max-h-[240px] overflow-y-auto pr-1"
								style={{ scrollbarWidth: 'thin', scrollbarColor: '#d7ad6c #f5ebe0' }}
							>
								{selectedPatients.map((patient) => {
									const pData = patientData[patient.id] || { actionType: '', priceFrom: '', priceTo: '' }
									const isCheckedForDailyNotes = patientsToDailyNotes.has(patient.personalId)
									const dailyNoteStatus = dailyNotesStatus?.[patient.personalId]

									return (
										<div key={patient.id} className="flex items-center gap-3">
											<div className="w-1/2 flex-shrink-0 flex items-center justify-between rounded-xl bg-gradient-to-r from-[#fdf8f0] to-[#f9f1e4] border border-[#e5c896] px-3 py-2.5 shadow-sm">
												<div className="flex items-center gap-2 min-w-0">
													{isEventToday && (
														<input
															type="checkbox"
															checked={isCheckedForDailyNotes}
															onChange={() => handleToggleDailyNotes(patient.personalId)}
															title={
																isCheckedForDailyNotes
																	? 'Pacient přišel – započítán do týdenního reportu. Odškrtnutím a uložením docházku zrušíte (záznam v Denním zápise zůstane).'
																	: 'Zaškrtnutím potvrdíte, že pacient přišel: vytvoří se záznam v Denním zápise a pacient se započítá do týdenního reportu.'
															}
															className="w-4 h-4 rounded border-2 border-[#d7ad6c] bg-white checked:bg-[#c49b5a] checked:border-[#c49b5a] focus:ring-[#d7ad6c] focus:ring-offset-0 cursor-pointer appearance-none relative checked:after:content-['✓'] checked:after:absolute checked:after:inset-0 checked:after:flex checked:after:items-center checked:after:justify-center checked:after:text-white checked:after:text-[11px] checked:after:font-bold flex-shrink-0"
														/>
													)}
													<span className="text-base text-[#3d3428] truncate">
														<span className="font-semibold">{patient.lastName} {patient.firstName}</span>
														<span className="text-[#8c8275] font-normal"> - {patient.personalId}</span>
														{isEventToday && dailyNoteStatus?.contains && (
															<span className="ml-1 text-xs text-[#d7ad6c]">(DZ)</span>
														)}
													</span>
												</div>
												<div className="flex items-center gap-0.5 flex-shrink-0">
													<a
														href={`/patient/${patient.id}`}
														target="_blank"
														rel="noopener noreferrer"
														onClick={(e) => e.stopPropagation()}
														title="Otevřít profil pacienta"
														className="p-1 rounded-lg text-[#8c8275] hover:text-[#d7ad6c] hover:bg-[#f5ebe0] transition-colors"
													>
														<ExternalLink className="h-4 w-4" />
													</a>
													<button
														type="button"
														onClick={() => handleRemovePatient(patient.id)}
														className="p-1 rounded-lg text-[#8c8275] hover:text-red-500 hover:bg-red-50 transition-colors"
													>
														<Trash2 className="h-4 w-4" />
													</button>
												</div>
											</div>

											<div className="flex-1 grid grid-cols-3 gap-2">
												{actionTypes.length > 0 ? (
													<Select
														value={pData.actionType || ''}
														onValueChange={(value) => handlePatientDataChange(patient.id, 'actionType', value)}
													>
														<SelectTrigger className={`${inputBase} h-12 text-base font-semibold`}>
															<SelectValue placeholder="Typ" />
														</SelectTrigger>
														<SelectContent className="bg-white border border-[#e8e4df] rounded-xl shadow-lg">
															{actionTypes.map((type) => {
																const isDisabled = type.value === 'pp' && isPPDisabled
																return (
																	<SelectItem
																		key={type.value}
																		value={type.value}
																		disabled={isDisabled}
																		className={`text-base text-[#3d3428] focus:bg-[#f5ebe0] focus:text-[#3d3428] ${isDisabled ? 'opacity-50' : ''}`}
																	>
																		{type.label}
																		{isDisabled && (
																			<span className="ml-1 text-xs text-[#8c8275]">(max {MAX_PP_PER_DAY})</span>
																		)}
																	</SelectItem>
																)
															})}
														</SelectContent>
													</Select>
												) : (
													<div />
												)}
												<Input
													type="number"
													placeholder="Cena od"
													value={pData.priceFrom}
													onChange={(e) => handlePatientDataChange(patient.id, 'priceFrom', e.target.value)}
													className={`${inputBase} h-12 text-base font-semibold`}
												/>
												<Input
													type="number"
													placeholder="Cena do"
													value={pData.priceTo}
													onChange={(e) => handlePatientDataChange(patient.id, 'priceTo', e.target.value)}
													className={`${inputBase} h-12 text-base font-semibold`}
												/>
											</div>
										</div>
									)
								})}
							</div>
							{showPatientsFade && (
								<div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-white to-transparent pointer-events-none rounded-b-xl transition-opacity" />
							)}
							</div>
						)}
					</div>

					{/* Notes - two columns */}
					<div className="grid grid-cols-2 gap-3">
						<Textarea
							id="note"
							placeholder="Poznámka..."
							{...register('note')}
							rows={3}
							className={`${inputBase} text-base resize-none`}
						/>
						<Textarea
							id="doctorsNote"
							placeholder="Poznámka ordinace..."
							{...register('doctorsNote')}
							rows={3}
							readOnly={viewMode === 'reception'}
							className={`${inputBase} text-base resize-none ${viewMode === 'reception' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
						/>
					</div>

					{/* Cancel SMS - conditional */}
					{isCancelledBg && (
						<>
							{mode === 'edit' && cancelSmsData && (
								<div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 w-fit">
									{cancelSmsData.status === 'DELIVERED' ? (
										<CheckCheck className="w-4 h-4 text-[#d7ad6c]" />
									) : cancelSmsData.status === 'SENT' ? (
										<Check className="w-4 h-4 text-[#d7ad6c]" />
									) : cancelSmsData.status === 'PENDING' ? (
										<Check className="w-4 h-4 text-[#e8e4df]" />
									) : null}
									<span className="text-base text-[#3d3428]">
										Zrušeno na základě:{' '}
										<span className="font-medium">
											{cancelSmsData.cancelType === 'call' && 'hovoru'}
											{cancelSmsData.cancelType === 'sms' && 'SMS zprávy'}
											{cancelSmsData.cancelType === 'person' && 'osobní návštěvy'}
											{cancelSmsData.cancelType === 'email' && 'emailu'}
										</span>
									</span>
								</div>
							)}

							{!cancelSmsData && (
								<div className="flex items-center gap-4 flex-wrap">
									<button
										type="button"
										onClick={() => {
											setSendCancelSms(!sendCancelSms)
											if (sendCancelSms) setCancelType('')
										}}
										className="flex items-center gap-2 hover:bg-[#f5ebe0] rounded-lg px-2 py-1 transition-colors cursor-pointer flex-shrink-0"
									>
										<div
											className={`w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center ${
												sendCancelSms ? 'bg-[#d7ad6c] border-[#d7ad6c]' : 'bg-white border-[#d7ad6c]'
											}`}
										>
											{sendCancelSms && <span className="text-white text-sm font-bold">✓</span>}
										</div>
										<span className="text-base text-[#3d3428] whitespace-nowrap">Odeslat SMS o zrušení</span>
									</button>

									{sendCancelSms && (
										<div className="flex items-center gap-3 flex-shrink-0">
											<span className="text-base text-[#5c5142] whitespace-nowrap">na základě:</span>
											<div className="flex rounded-xl overflow-hidden border border-gray-300">
												{(
													[
														{ value: 'call', label: 'Hovoru' },
														{ value: 'sms', label: 'SMS' },
														{ value: 'person', label: 'Osobně' },
														{ value: 'email', label: 'Emailu' },
													] as const
												).map((option) => (
													<button
														key={option.value}
														type="button"
														onClick={() => setCancelType(option.value)}
														className={`px-3 py-1.5 text-base transition-colors cursor-pointer ${
															cancelType === option.value
																? 'bg-[#d7ad6c] text-white'
																: 'bg-white text-[#3d3428] hover:bg-[#f5ebe0]'
														}`}
													>
														{option.label}
													</button>
												))}
											</div>
										</div>
									)}
								</div>
							)}
						</>
					)}

					{/* Bottom bar: color swatches + actions in one row */}
					<div className="flex items-center justify-between gap-4 pt-3 border-t border-[#e8e4df]">
						<ColorPicker
							value={background || DEFAULT_EVENT_COLOR}
							onChange={(color) => setValue('background', color)}
						/>
						<div className="flex items-center gap-3 flex-shrink-0">
							{mode === 'edit' && (
								<Button
									type="button"
									variant="outline"
									onClick={handleDelete}
									disabled={isLoading || isDeleting}
									className="h-11 px-5 rounded-xl bg-red-500/10 border-red-300 text-red-600 hover:bg-red-500/20 hover:border-red-400 transition-all text-base"
								>
									<Trash2 className="mr-2 h-4 w-4" />
									Smazat
								</Button>
							)}
							<Button
								type="button"
								variant="outline"
								onClick={onClose}
								className="h-11 px-6 rounded-xl bg-white border border-[#e8e4df] text-[#5c5142] hover:bg-[#f5ebe0] hover:border-[#d7ad6c] transition-all text-base"
							>
								Zrušit
							</Button>
							<Button
								type="submit"
								disabled={isLoading}
								className="h-11 px-6 rounded-xl bg-[#d7ad6c] text-white hover:bg-[#c49b5a] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md text-base"
							>
								{isLoading
									? 'Ukládání...'
									: mode === 'create'
										? 'Vytvořit termín'
										: 'Uložit změny'}
							</Button>
						</div>
					</div>
				</form>
					</div>

					{showGhostColumn && ghostCalendarType && (
						<GhostDayColumn calendarType={ghostCalendarType} date={selectedDate} />
					)}
				</div>
			</DialogContent>
		</Dialog>
	)
}
